import { useCallback } from 'react'

import { useMutation } from '@tanstack/react-query'
import {
  type MergeArgs,
  type LegacyMergeRequestBody,
  type MergeResponse
} from './types'
import useMapConflictSelections from 'hooks/useMapConflictSelections/useMapConflictSelections'
import { type ConflictVersion } from 'components/ConflictVersionSelector/types'
import useDesignerClient from 'api/clients/useDesignerClient'
import useGitContext from '../../../provider/GitProvider/useGitContext'
import { queryKeys } from '../../query-keys'

const conflictMapping = new Map<ConflictVersion, string>([
  ['OURS', 'SOURCE'],
  ['THEIRS', 'TARGET']
])

const usePostMergeMutationQueryKey = () => {
  const { project } = useGitContext()
  return [queryKeys.merge, project]
}
const useLegacyMerge = () => {
  const { project, client } = useDesignerClient()
  const { map } = useMapConflictSelections(conflictMapping)

  const merge = useCallback(
    async ({
      mergeMessage,
      targetBranch,
      sourceCommitId,
      targetCommitId,
      conflictSelections
    }: MergeArgs) => {
      const body: LegacyMergeRequestBody = {
        sourceCommitId,
        targetCommitId,
        targetBranchName: targetBranch,
        commitMessage: mergeMessage,
        conflictResolutions: map(conflictSelections)
      }

      const url = `projects/${project}/merges`
      const { data } = await client.post<MergeResponse>(url, body)

      return data
    },
    [client, map, project]
  )

  return useMutation(usePostMergeMutationQueryKey(), merge, {
    meta: {
      error: {
        title: 'api.toast.merge.title',
        message: 'api.toast.merge.message'
      }
    }
  })
}

export default useLegacyMerge
